import ModalCourseCatalog from './components/ModalCourseDetail.vue';
import { mapGetters, mapActions } from 'vuex';
import { compactNumberFormatter } from '@/utils/currencyFormatter.js';

export default {
  name: "CourseCatalog",
  components: {
    ModalCourseCatalog
  },
  computed: {
    ...mapGetters('courseCatalog', {
      courses: 'getCourses',
      coursesTotalItems: 'getTotalItems',
      isLoadingCourses: 'getLoading',
      courseDetail: 'getCourseDetail'
    }),
    visiblePages() {
      const pages = [];
      const totalPages = this.totalPages;
      const currentPage = this.body.page;
      
      // If total pages is less than or equal to 7, show all pages
      if (totalPages <= 7) {
        for (let i = 1; i <= totalPages; i++) {
          pages.push(i);
        }
        return pages;
      }
      
      // Always show first page
      pages.push(1);
      
      // Calculate range around current page
      if (currentPage <= 3) {
        // Near the beginning: show 1, 2, 3, 4, ..., last
        for (let i = 2; i <= 4; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        // Near the end: show 1, ..., last-3, last-2, last-1, last
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // In the middle: show 1, ..., current-1, current, current+1, ..., last
        pages.push('...');
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push('...');
        pages.push(totalPages);
      }
      
      return pages;
    },
    totalPages() {
      return Math.ceil(this.coursesTotalItems / this.body.limit);
    },
    totalItems() {
      return this.coursesTotalItems;
    },
    isLoading() {
      return this.isLoadingCourses;
    },
    activeFiltersCount() {
      let count = 0;
      if (this.filterModal.training_scope) count++;
      if (this.filterModal.platform) count++;
      if (this.filterModal.role_competency_id) count++;
      if (this.filterModal.competency_type) count++;
      if (this.filterModal.competency_id) count++;
      return count;
    }
  },
  data() {
    return {
      imageBaseUrl: process.env.VUE_APP_API_URL_UGEMS_UNIV,
      selectedCompetency: null,
      selectedCourse: null,
      courseToDelete: null,
      selectedBatch: null,
      isDownloading: false,
      isDownloadingHistoryCatalog: false,
      listTabMenu: {
        privilages: {
          create: true,
          read: true,
          update: true,
          delete: true
        }
      },
      perPageOptions: [10, 50, 100],
      body: {
        page: 1,
        limit: 10,
        search: "",
        order: "",
        is_draft: false, // Only show published courses
        // Filter parameters
        training_scope: "",
        platform: "",
        role_competency_id: "",
        competency_type: "",
        competency_id: ""
      },
      filters: {
        search: ""
      },
      filterModal: {
        training_scope: "",
        platform: "",
        role_competency_id: "",
        competency_type: "",
        competency_id: ""
      },
      searchTimeout: null,
      // Dummy data for static display (will be replaced by API data)
      courseData: [],
      // Upload related data
      selectedFile: null,
      isUploading: false,
      uploadMode: 'catalog',
    };
  },
  mounted() {
    this.setMenuPermissions();
    // Load courses from API
    this.loadCourses();
  },
  methods: {
    ...mapActions('courseCatalog', ['getCourses', 'getCourseById', 'cloneCourse']),
    compactNumberFormatter,
    // Load courses from API
    async loadCourses() {
      try {
        // Clean params - remove empty strings to avoid sending unnecessary params
        const cleanParams = Object.entries(this.body).reduce((acc, [key, value]) => {
          if (value !== "" && value !== null && value !== undefined) {
            acc[key] = value;
          }
          return acc;
        }, {});
        
        await this.getCourses(cleanParams);
        this.courseData = this.courses
      } catch (error) {
        console.error('Error loading courses:', error);
        this.$swal('Error', 'Daftar pelatihan belum berhasil dimuat', 'error');
      }
    },

    // Format price with thousand separator
    formatPrice(price) {
      if (!price) return '0';
      return price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    },
    // Set menu permissions
    setMenuPermissions() {
      const listMenu = JSON.parse(localStorage.getItem('listMenu') || '[]');
      const currentPath = window.location.pathname;
      const menu = listMenu.find((menu) =>
        menu.link === String(currentPath) || menu.children.some((child) => child.link === String(currentPath))
      );
      this.listTabMenu = menu ? menu.children.find((item) => item.link === currentPath) || menu : {};
    },

    // Get badge class based on course type
    getCourseTypeBadgeClass(type) {
      const badges = {
        'Technical': 'badge badge-primary',
        'Soft Skills': 'badge badge-success',
        'Leadership': 'badge badge-warning',
        'Compliance': 'badge badge-info',
        'Safety': 'badge badge-secondary'
      };
      return badges[type] || 'badge badge-secondary';
    },

    // Get schedule badge class
    getScheduleBadgeClass(schedule_status) {
      if (schedule_status === 'SCHEDULE') {
        return 'badge bg-success';
      }
      return 'badge bg-danger';
    },

    // Get icon class based on training type
    getTrainingTypeIcon(trainingType) {
      const type = trainingType?.toLowerCase() || '';
      
      if (type.includes('certification') && !type.includes('semi')) {
        return 'fe fe-file-text'; // Certification
      } else if (type.includes('semi')) {
        return 'fe fe-shield'; // Semi Certification
      } else if (type.includes('training')) {
        return 'fe fe-user'; // Training
      }
      
      return 'fe fe-award'; // Default icon
    },

    // Handle image error
    handleImageError(event) {
      event.target.src = 'https://dummyimage.com/300x200/FAFAFA/000000&text=Course';
    },
    handleImageProviderError(event) {
      event.target.src = 'https://dummyimage.com/16x16/ffffff/000000&text=Provider+title';
    },

    // Search with debounce
    onSearch() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      
      this.searchTimeout = setTimeout(() => {
        this.body.search = this.filters.search;
        this.body.page = 1;
        this.loadCourses();
      }, 500);
    },

    // Show filter modal
    showFilterModal() {
      this.$bvModal.show('modal-filter');
    },

    // Apply filters
    applyFilters(filters) {
      // Update filterModal with received filters
      this.filterModal = { ...filters };
      
      // Update body with filter parameters
      this.body.page = 1;
      this.body.training_scope = filters.training_scope || "";
      this.body.platform = filters.platform || "";
      this.body.role_competency_id = filters.role_competency_id || "";
      this.body.competency_type = filters.competency_type || "";
      this.body.competency_id = filters.competency_id || "";
      
      // Call API with updated filters
      this.loadCourses();
      
      this.$bvModal.hide('modal-filter');
      this.$swal("Success", "Filter berhasil diterapkan", "success");
    },

    // Reset filters
    resetFilters() {
      this.filterModal = {
        training_scope: "",
        platform: "",
        role_competency_id: "",
        competency_type: "",
        competency_id: ""
      };
      
      // Clear filters from body
      this.body.training_scope = "";
      this.body.platform = "";
      this.body.role_competency_id = "";
      this.body.competency_type = "";
      this.body.competency_id = "";
      this.body.page = 1;
      
      // Reload courses without filters
      this.loadCourses();
    },

    // Clear single filter
    clearSingleFilter(filterKey) {
      this.filterModal[filterKey] = "";
      this.body[filterKey] = "";
      this.body.page = 1;
      this.loadCourses();
    },

    // Clear all filters (including search)
    clearFilters() {
      this.filters = {
        search: ""
      };
      this.filterModal = {
        training_scope: "",
        platform: "",
        role_competency_id: "",
        competency_type: "",
        competency_id: ""
      };
      
      // Clear all filter parameters from body
      this.body.search = "";
      this.body.training_scope = "";
      this.body.platform = "";
      this.body.role_competency_id = "";
      this.body.competency_type = "";
      this.body.competency_id = "";
      this.body.page = 1;
      
      // Reload courses
      this.loadCourses();
    },

    // Actions
    addData() {
      this.$bvModal.show('modal-add-course');
    },

    showCourseDetail(course) {
      this.selectedCourse = course;
      this.$bvModal.show('modal-course-detail');
    },

    viewData(course) {
      console.log('View course:', course);
      this.showCourseDetail(course);
    },

    updateData(course) {
      console.log('Edit course:', course);
      // Will open modal for editing course
    },

    editData(course) {
      console.log('Edit course:', course);
      // Will open modal for editing course
    },

    deleteData(course) {
      this.$swal({
        title: "Apakah Anda yakin?",
        text: `Apakah Anda yakin ingin menghapus pelatihan "${course.title}"?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#00D97E",
        cancelButtonColor: "#E63757",
        confirmButtonText: "Ya, hapus",
        cancelButtonText: "Batal"
      }).then((result) => {
        if (result.isConfirmed) {
          console.log('Delete course:', course);
          this.$swal("Terhapus", "Pelatihan berhasil dihapus", "success");
        }
      });
    },

    // Download template
    downloadTemplate() {
      console.log('Download template');
      this.$swal("Info", "Fitur unduh template belum tersedia", "info");
    },

    // Show upload modal
    showUploadModal() {
      console.log('Show upload modal');
      this.$swal("Info", "Fitur unggah belum tersedia", "info");
    },

    // Pagination
    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.body.page = page;
        this.loadCourses();
      }
    },

    onPerPageChange() {
      this.body.page = 1;
      this.loadCourses();
    },

    // Modal event handlers
    onCourseSelected(course) {
      console.log('Course selected from modal:', course);
    },

    // New modal methods
    editCourse(courseData) {
      // Call editCourseFromDetail method
      this.editCourseFromDetail(courseData);
    },

    // ✅ Handle delete dari modal detail
    showDeleteConfirmation(courseData) {
      // Store course yang akan dihapus (bisa dari modal detail atau list)
      this.courseToDelete = courseData || this.selectedCourse;
      this.$bvModal.show('modal-delete-confirmation');
    },

    // ✅ Handle delete dari card list
    deleteFromList(course) {
      this.courseToDelete = course;
      this.$bvModal.show('modal-delete-confirmation');
    },

    // ✅ Handle setelah delete success dari ModalDeleteConfirmation
    async onDeleteSuccess() {
      // Close modal detail jika sedang terbuka
      this.$bvModal.hide('modal-course-detail');
      
      // Refresh course list
      await this.loadCourses();
      
      // Clear selected course
      this.courseToDelete = null;
      this.selectedCourse = null;
    },

    // ✅ Open confirmation course modal with batch data
    openConfirmationCourse(batchData) {
      console.log('✅ openConfirmationCourse called with:', batchData);
      this.selectedBatch = batchData;
      console.log('📝 selectedBatch set to:', this.selectedBatch);
      
      // Open modal with delay to ensure data is set
      this.$nextTick(() => {
        console.log('⏰ Opening modal-confirmation-course...');
        this.$bvModal.show('modal-confirmation-course');
      });
    },

    // ✅ Open confirmation batch modal with batch data
    openConfirmationDeleteBatch(batchData) {
      console.log('✅ openConfirmationBatch called with:', batchData);
      this.selectedBatch = batchData;
      console.log('📝 selectedBatch set to:', this.selectedBatch);
      
      // Open modal with delay to ensure data is set
      this.$nextTick(() => {
        console.log('⏰ Opening modal-confirmation-batch...');
        this.$bvModal.show('modal-delete-confirmation-batch');
      });
    },

    // ✅ Handle after confirmation submitted successfully
    async onConfirmationSuccess() {
      console.log('✅ Confirmation submitted successfully');
      
      // Refresh course detail to get updated batch data
      if (this.selectedCourse && this.selectedCourse.id) {
        await this.getCourseById(this.selectedCourse.id);
      }
      
      // Clear selected batch
      this.selectedBatch = null;
      
      // Show success notification (already shown in modal, this is optional)
      // this.$swal('Success', 'Realisasi event pelatihan berhasil dikirim', 'success');
    },

    onCourseCreated(courseData) {
      console.log('New course created:', courseData);
      // Refresh the course list
      this.loadCourses();
    },

    onCourseSaved() {
      this.loadCourses();
    },

    // Edit course from list (with ID, need to fetch detail)
    async editCourseFromList(course) {
      try {
        // Load course detail from API
        await this.getCourseById(course.id);
        const courseDetail = this.courseDetail;
        
        if (courseDetail) {
          // Open modal and pass data
          this.$root.$emit('open-edit-course', { 
            mode: 'edit',
            data: courseDetail 
          });
          this.$bvModal.show('modal-add-course');
        }
      } catch (error) {
        console.error('Error loading course detail for edit:', error);
        this.$swal('Error', 'Detail pelatihan belum berhasil dimuat', 'error');
      }
    },

    // Edit course from detail modal (already have full data)
    editCourseFromDetail(courseData) {
      // Close detail modal
      this.$bvModal.hide('modal-course-detail');
      
      // Wait a bit for modal to close, then open edit modal
      setTimeout(() => {
        // Emit event to populate form
        this.$root.$emit('open-edit-course', {
          mode: 'edit',
          data: courseData
        });

        // Open edit modal
        this.$bvModal.show('modal-add-course');
      }, 300); // Small delay to ensure smooth transition
    },

    async cloneCourseTemplate(course) {
      try {
        // Show confirmation
        const result = await this.$swal({
          title: 'Duplikasi Template Pelatihan?',
          text: `Apakah Anda yakin ingin menduplikasi "${course.title}"?`,
          icon: 'question',
          showCancelButton: true,
          confirmButtonText: 'Ya, duplikasi',
          cancelButtonText: 'Batal',
          confirmButtonColor: '#e63757',
          cancelButtonColor: '#6c757d'
        });

        if (result.isConfirmed) {
          // Call API clone
          const response = await this.cloneCourse(course.id);
          
          // Show success message
          this.$swal('Success', 'Template pelatihan berhasil diduplikasi', 'success');
          
          // Refresh course list
          await this.loadCourses();
          
          // Optional: Open the cloned course for edit
          if (response.data && response.data.id) {
            await this.editCourseFromList({ id: response.data.id });
          }
        }
      } catch (error) {
        console.error('Error cloning course:', error);
        this.$swal('Error', 'Template pelatihan belum berhasil diduplikasi', 'error');
      }
    },

    showUploadModal() {
      this.uploadMode = 'catalog';
      this.selectedFile = null;
      this.isUploading = false;
      this.$bvModal.show('modal-upload');
    },

    showHistoryUploadModal() {
      this.uploadMode = 'historyCatalog';
      this.selectedFile = null;
      this.isUploading = false;
      this.$bvModal.show('modal-upload');
    },

    onFileChange(event) {
      const file = event.target.files[0];
      if (!file) {
        this.selectedFile = null;
        return;
      }

      if (!file.name.toLowerCase().endsWith('.xlsx')) {
        this.$swal('Error', 'Pilih file Excel yang valid (.xlsx)', 'error');
        this.clearFile();
        return;
      }

      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        this.$swal('Error', 'Ukuran file harus kurang dari 5MB', 'error');
        this.clearFile();
        return;
      }

      this.selectedFile = file;
    },

    clearFile() {
      this.selectedFile = null;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    async downloadTemplate() {
      if (this.isDownloading) return;
      
      this.isDownloading = true;
      try {
        const response = await this.$store.dispatch('courseCatalog/downloadTemplate');
        
        const blob = new Blob([response.data], { 
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'Course Catalog Bulk Upload Template.xlsx');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        this.$swal({
          title: 'Success',
          text: 'Template berhasil diunduh',
          icon: 'success',
          confirmButtonText: 'OK',
          confirmButtonColor: '#2c7be5'
        });
      } catch (error) {
        console.error('Error downloading template:', error);
        this.$swal({
          title: 'Error',
          text: 'Template belum berhasil diunduh. Silakan coba lagi.',
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#e63757'
        });
      } finally {
        this.isDownloading = false;
      }
    },

    async downloadHistoryCatalogTemplate() {
      if (this.isDownloadingHistoryCatalog) return;
      
      this.isDownloadingHistoryCatalog = true;
      try {
        const response = await this.$store.dispatch('courseCatalog/downloadHistoryCatalogTemplate');
        
        const blob = new Blob([response.data], { 
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'History Catalog Bulk Upload Template.xlsx');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        this.$swal({
          title: 'Success',
          text: 'Template history catalog berhasil diunduh',
          icon: 'success',
          confirmButtonText: 'OK',
          confirmButtonColor: '#2c7be5'
        });
      } catch (error) {
        console.error('Error downloading history catalog template:', error);
        this.$swal({
          title: 'Error',
          text: 'Template history catalog belum berhasil diunduh. Silakan coba lagi.',
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#e63757'
        });
      } finally {
        this.isDownloadingHistoryCatalog = false;
      }
    },

    async uploadFile() {
      if (!this.selectedFile) {
        this.$swal('Error', 'Pilih file yang akan diunggah', 'error');
        return;
      }

      this.isUploading = true;
      
      try {
        const formData = new FormData();
        formData.append('file', this.selectedFile);

        const action = this.uploadMode === 'historyCatalog'
          ? 'courseCatalog/bulkCreateHistoryTrainings'
          : 'courseCatalog/bulkCreateTrainings';
        const response = await this.$store.dispatch(action, formData);
        
        if (response && response.data) {
          const { 
            importedTrainings = 0, 
            failedTrainings = 0, 
            successDetails = [], 
            errors = [] 
          } = response.data;
          
          if (failedTrainings > 0 && errors.length > 0) {
            let errorTableHtml = `
              <div class="mb-3 text-left">
                <p><strong>Import Summary:</strong></p>
                <ul>
                  <li>Imported Trainings: ${importedTrainings}</li>
                  <li>Failed Trainings: ${failedTrainings}</li>
                </ul>
              </div>
              <div class="table-responsive">
                <table class="table table-sm table-bordered">
                  <thead class="table-danger">
                    <tr>
                      <th>Row</th>
                      <th>Message</th>
                      <th>Detail</th>
                    </tr>
                  </thead>
                  <tbody>
            `;
            
            errors.forEach(error => {
              errorTableHtml += `
                <tr>
                  <td>${error.row || '-'}</td>
                  <td>${error.message || 'Unknown error'}</td>
                  <td>${error.detail || '-'}</td>
                </tr>
              `;
            });
            
            errorTableHtml += `
                  </tbody>
                </table>
              </div>
            `;
            
            this.$swal({
              title: 'Unggah Selesai dengan Error',
              html: errorTableHtml,
              icon: 'warning',
              confirmButtonText: 'OK',
              width: '600px'
            }).then(() => {
              this.loadCourses(); // Refresh data table
              this.$bvModal.hide('modal-upload');
              this.clearFile();
            });
          } 
          // If no errors, show success message
          else {
            let successTableHtml = `
              <div class="mb-3 text-left">
                <p><strong>Import Summary:</strong></p>
                <ul>
                  <li>Imported Trainings: ${importedTrainings}</li>
                  <li>Failed Trainings: ${failedTrainings}</li>
                </ul>
              </div>
            `;
            
            // Add success details table if available
            if (successDetails.length > 0) {
              successTableHtml += `
                <div class="table-responsive">
                  <table class="table table-sm table-bordered">
                    <thead class="table-success">
                      <tr>
                        <th>Row</th>
                        <th>Training ID</th>
                        <th>Title</th>
                      </tr>
                    </thead>
                    <tbody>
              `;
              
              successDetails.forEach(detail => {
                successTableHtml += `
                  <tr>
                    <td>${detail.row || '-'}</td>
                    <td>${detail.training_id || '-'}</td>
                    <td>${detail.title || '-'}</td>
                  </tr>
                `;
              });
              
              successTableHtml += `
                    </tbody>
                  </table>
                </div>
              `;
            }
            
            this.$swal({
              title: 'Unggah Berhasil',
              html: successTableHtml,
              icon: 'success',
              confirmButtonText: 'OK',
              width: '600px'
            }).then(() => {
              this.loadCourses(); // Refresh data table
              this.$bvModal.hide('modal-upload');
              this.clearFile();
            });
          }
        } else {
          // Fallback for unexpected response format
          this.$swal({
            title: 'Success',
            text: 'File berhasil diunggah',
            icon: 'success',
            confirmButtonText: 'OK'
          }).then(() => {
            this.loadCourses();
            this.$bvModal.hide('modal-upload');
            this.clearFile();
          });
        }

      } catch (error) {
        console.error('Error uploading file:', error);
        let errorMessage = 'Error while uploading file';
        
        // Handle API error response
        if (error.response && error.response.data) {
          const errorData = error.response.data;
          if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.error) {
            errorMessage = errorData.error;
          }
        }
        
        this.$swal('Error', errorMessage, 'error');
      } finally {
        this.isUploading = false;
      }
    }
  }
};
